import React, { useState, useEffect, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from 'recharts';
import { X } from 'lucide-react';
import { apiGet } from '../../lib/apiClient';

export default function AccountDrawdownHistory({ equityCurve = [], accountId = 'all', typeFilter = 'all', balance = 0 }) {
  const [mode, setMode] = useState('DD');     // DD | Float
  const [unit, setUnit] = useState('%');      // $ | %
  const [period, setPeriod] = useState('ALL');
  const [spikes, setSpikes] = useState([]);
  const [selectedSpike, setSelectedSpike] = useState(null);

  // Fetch top 5 spikes
  useEffect(() => {
    const aid = accountId !== 'all' ? `&account_id=${accountId}` : '';
    const tf = typeFilter !== 'all' ? `&type_filter=${typeFilter}` : '';
    apiGet(`/api/drawdown-spikes/top?spike_type=${mode.toLowerCase() === 'dd' ? 'dd' : 'float'}${aid}${tf}&limit=5`)
      .then(data => setSpikes(Array.isArray(data) ? data : []))
      .catch(() => setSpikes([]));
  }, [mode, accountId, typeFilter]);

  // Filter by period
  const filteredData = useMemo(() => {
    if (!equityCurve || equityCurve.length === 0) return [];
    if (period === 'ALL') return equityCurve;
    const now = new Date();
    let days = 30;
    switch (period) { case '1D': days=1; break; case '1W': days=7; break; case '1M': days=30; break; case '3M': days=90; break; }
    const cutoff = new Date(now.getTime() - days * 86400000);
    return equityCurve.filter(d => d.date && new Date(d.date) >= cutoff);
  }, [equityCurve, period]);

  // Calculate drawdown chart data
  const chartData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];

    // Reconstruct actual equity: starting_balance + cumulative_profit
    const lastEquity = filteredData[filteredData.length - 1]?.equity || 0;
    const startingBalance = balance > 0 ? (balance - lastEquity) : 10000; // fallback

    let peak = 0;
    return filteredData.map(d => {
      const actualEquity = startingBalance + (d.equity || 0);
      if (actualEquity > peak) peak = actualEquity;
      const ddDollar = actualEquity - peak; // negative when in DD
      const ddPct = peak > 0 ? (ddDollar / peak) * 100 : 0;
      return { date: d.date, dd_dollar: ddDollar, dd_pct: ddPct };
    });
  }, [filteredData, balance]);

  const dataKey = unit === '%' ? 'dd_pct' : 'dd_dollar';
  const yFormatter = unit === '%' ? (v) => `${v.toFixed(1)}%` : (v) => v === 0 ? '$0' : `$${(v/1000).toFixed(1)}K`;

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* DD / Float toggle */}
          <div style={{ display: 'flex', background: '#21262d', borderRadius: '6px', overflow: 'hidden' }}>
            <button onClick={() => setMode('DD')} style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 700, border: 'none', cursor: 'pointer', background: mode === 'DD' ? '#378ADD' : 'transparent', color: mode === 'DD' ? '#fff' : '#8b949e' }}>DD</button>
            <button onClick={() => setMode('Float')} style={{ padding: '4px 12px', fontSize: '11px', fontWeight: 700, border: 'none', cursor: 'pointer', background: mode === 'Float' ? '#378ADD' : 'transparent', color: mode === 'Float' ? '#fff' : '#8b949e' }}>Float</button>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {/* $ / % */}
          <div style={{ display: 'flex', background: '#21262d', borderRadius: '6px', overflow: 'hidden' }}>
            <button onClick={() => setUnit('$')} style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 700, border: 'none', cursor: 'pointer', background: unit === '$' ? '#00ff88' : 'transparent', color: unit === '$' ? '#0d1117' : '#8b949e' }}>$</button>
            <button onClick={() => setUnit('%')} style={{ padding: '4px 10px', fontSize: '10px', fontWeight: 700, border: 'none', cursor: 'pointer', background: unit === '%' ? '#00ff88' : 'transparent', color: unit === '%' ? '#0d1117' : '#8b949e' }}>%</button>
          </div>
          {/* Period */}
          <div style={{ display: 'flex', background: '#21262d', borderRadius: '6px', overflow: 'hidden' }}>
            {['1D','1W','1M','3M','ALL'].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{ padding: '4px 8px', fontSize: '10px', fontWeight: period === p ? 700 : 500, border: 'none', cursor: 'pointer', background: period === p ? '#00ff88' : 'transparent', color: period === p ? '#0d1117' : '#8b949e' }}>{p}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Subtitle */}
      <p style={{ fontSize: '11px', color: '#8b949e', marginBottom: '8px' }}>
        {mode === 'Float' ? `Floating P/L — live tracking (${chartData.length} readings)` : 'Data source: EA Native (tick-level)'}
      </p>

      {/* Chart with red color */}
      {chartData.length > 0 ? (
        <div style={{ position: 'relative' }}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="ddSpikeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ff4757" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#ff4757" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis dataKey="date" stroke="#8b949e" tick={{ fill: '#8b949e', fontSize: 9 }} />
              <YAxis stroke="#8b949e" tick={{ fill: '#8b949e', fontSize: 9 }} tickFormatter={yFormatter} />
              <Tooltip
                contentStyle={{ backgroundColor: '#161b22', border: '1px solid #21262d', borderRadius: '8px' }}
                formatter={(v) => [unit === '%' ? `${Number(v).toFixed(2)}%` : `$${Number(v).toFixed(2)}`, 'Drawdown']}
              />
              <Area type="monotone" dataKey={dataKey} stroke="#ff4757" fill="url(#ddSpikeGrad)" strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p style={{ color: '#484f58', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No drawdown data available</p>
      )}

      {/* Top 5 Spike List */}
      {spikes.length > 0 && (
        <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {spikes.map((spike, i) => (
            <div
              key={spike.id || i}
              onClick={() => setSelectedSpike(spike)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', borderRadius: '8px', cursor: 'pointer',
                background: '#161b22', border: '1px solid #21262d',
                transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#ff4757'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#21262d'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ background: '#ff4757', color: '#fff', fontSize: '10px', fontWeight: 700, width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>#{spike.rank}</span>
                <div>
                  <span style={{ color: '#ff4757', fontSize: '14px', fontWeight: 700 }}>{Math.abs(spike.dd_percent).toFixed(2)}%</span>
                  <span style={{ color: '#8b949e', fontSize: '11px', marginLeft: '8px' }}>
                    {spike.recorded_at ? new Date(spike.recorded_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
              </div>
              <span style={{ color: '#ff4757', fontSize: '13px', fontWeight: 600 }}>
                -${Math.abs(spike.dd_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Spike Detail Popup */}
      {selectedSpike && (
        <SpikeDetailPopup spike={selectedSpike} onClose={() => setSelectedSpike(null)} />
      )}
    </div>
  );
}

function SpikeDetailPopup({ spike, onClose }) {
  const breakdown = spike.ea_breakdown || [];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }} onClick={onClose}>
      <div style={{ background: '#0d1117', border: '1px solid #21262d', borderRadius: '16px', padding: '24px', width: '90vw', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ background: '#ff4757', color: '#fff', fontSize: '12px', fontWeight: 700, width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>#{spike.rank}</span>
            <div>
              <h3 style={{ color: '#e6edf3', fontSize: '16px', fontWeight: 700, margin: 0 }}>Floating Drawdown Breakdown</h3>
              <p style={{ color: '#8b949e', fontSize: '12px', margin: 0 }}>
                {spike.recorded_at ? new Date(spike.recorded_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b949e' }}>
            <X size={20} />
          </button>
        </div>

        {/* DD Stats */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', fontSize: '12px' }}>
          <span style={{ color: '#ff4757' }}>↘ Drawdown: <strong>{Math.abs(spike.dd_percent).toFixed(2)}%</strong></span>
          <span style={{ color: '#8b949e' }}>↘ Net floating: <strong style={{ color: '#ff4757' }}>-${Math.abs(spike.dd_amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></span>
        </div>

        {/* EA Breakdown */}
        <div style={{ marginBottom: '16px' }}>
          <p style={{ color: '#8b949e', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>
            EAS HOLDING POSITIONS AT THIS MOMENT ({breakdown.length})
          </p>

          {breakdown.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {breakdown.map((ea, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#161b22', border: '1px solid #21262d', borderRadius: '8px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ color: '#e6edf3', fontSize: '13px', fontWeight: 700 }}>{ea.ea_name || `Magic ${ea.magic}`}</span>
                      <span style={{ background: '#00ff8820', color: '#00ff88', fontSize: '9px', fontWeight: 600, padding: '2px 6px', borderRadius: '4px' }}>⚡ STILL ACTIVE</span>
                    </div>
                    <p style={{ color: '#484f58', fontSize: '10px', margin: '2px 0 0' }}>
                      magic: {ea.magic}
                    </p>
                  </div>
                  <span style={{ color: ea.floating_pnl >= 0 ? '#00ff88' : '#ff4757', fontSize: '14px', fontWeight: 700 }}>
                    {ea.floating_pnl >= 0 ? '+' : '-'}${Math.abs(ea.floating_pnl).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#484f58', fontSize: '12px', textAlign: 'center', padding: '20px 0' }}>
              No per-EA breakdown available for this spike.
            </p>
          )}
        </div>

        {/* Footer note */}
        <div style={{ background: '#161b22', borderRadius: '8px', padding: '12px', marginTop: '12px' }}>
          <p style={{ color: '#484f58', fontSize: '10px', lineHeight: 1.5, margin: 0 }}>
            Closed trades show their floating P/L reconstructed from M1 price bars at this exact timestamp. Rows tagged <span style={{ color: '#00ff88', fontWeight: 600 }}>Still active</span> are using the trade's live floating P/L right now (no historical price extrapolation), so their number reflects the current position rather than what it was at the spike moment.
          </p>
        </div>
      </div>
    </div>
  );
}
